import { useState, useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, CheckCircle } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

export default function SetupAccountPage() {
  const { t } = useTranslation();
  const [, params] = useRoute("/setup-account/:email");
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    // Decode email from URL parameter
    if (params?.email) {
      const decodedEmail = decodeURIComponent(params.email);
      setEmail(decodedEmail);
    }
  }, [params]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validatePasswords = () => {
    if (formData.password.length < 6) {
      toast({
        title: t("setupAccount.errors.passwordTooShort"),
        description: t("setupAccount.errors.passwordMinLength"),
        variant: "destructive",
      });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: t("setupAccount.errors.passwordsDoNotMatch"),
        description: t("setupAccount.errors.passwordsMustMatch"),
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswords()) return;

    setIsLoading(true);

    try {
      await apiRequest("POST", "/api/setup-account", {
        email,
        password: formData.password,
      });

      toast({
        title: t("setupAccount.success.title"),
        description: t("setupAccount.success.description"),
      });

      const isProduction =
        window.location.hostname.includes("viral.partners") ||
        window.location.hostname.includes("gawronify.com");

      setTimeout(() => {
        if (isProduction) {
          window.location.href = "https://ai.gawronify.com/";
        } else {
          setLocation("/");
        }
      }, 2000);
    } catch (error: any) {
      toast({
        title: t("setupAccount.errors.retryTitle"),
        description: t("setupAccount.errors.retryDesc"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-[#030407] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/[0.04] backdrop-blur-sm border-white/[0.08]">
          <CardContent className="pt-6">
            <div className="text-center text-white/60">{t("setupAccount.invalidLink")}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030407] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[#0a0c10]/80 backdrop-blur-sm border-white/[0.08]">
        <CardHeader className="text-center space-y-4">
          <img
            src="https://pub-9c0a0846a5c24484849bf7e3f13e4239.r2.dev/viralpartners/logo-800w.webp"
            alt="Viral Partners Logo"
            className="mx-auto h-16 object-contain"
          />
          <CardTitle className="text-heading-2 font-extrabold bg-gradient-to-r from-red-400 to-red-300 bg-clip-text text-transparent">
            {t("setupAccount.title")}
          </CardTitle>
          <div className="space-y-2">
            <div className="flex items-center justify-center space-x-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm">{t("setupAccount.paymentConfirmed")}</span>
            </div>
            <p className="text-white/60 text-sm">
              {t("setupAccount.configurePasswordFor")}{" "}
              <span className="text-white font-medium">{email}</span>
            </p>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">
                {t("setupAccount.email")}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                disabled
                className="bg-white/[0.04] border-white/[0.1] text-white/60 cursor-not-allowed"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">
                {t("setupAccount.newPassword")}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  className="bg-white/[0.06] border-white/[0.1] text-white pr-10"
                  placeholder={t("setupAccount.minSixChars")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white/70"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/70">
                {t("setupAccount.confirmPassword")}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  className="bg-white/[0.06] border-white/[0.1] text-white pr-10"
                  placeholder={t("setupAccount.confirmPasswordPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white/70"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
              disabled={isLoading}
              data-testid="button-setup-account"
            >
              {isLoading ? t("setupAccount.configuring") : t("setupAccount.createAccount")}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-white/40">{t("setupAccount.redirectHint")}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
